import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  getTransactionHistory,
  clearTransactionHistory,
  clearError
} from "../store/slices/transactionSlice";
import { showApiError } from "../utils/toast";

export const useTransactionHistory = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { 
    transactionHistory,
    historyMeta,
    historyLoading,
    error
  } = useSelector((state) => state.transaction);

  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "all",
    search: "",
    page: 1,
    perPage: 100 // Fetch more records for client-side pagination
  });

  // Sync filters with URL parameters - but don't trigger API calls for page changes
  useEffect(() => {
    const currentPage = parseInt(searchParams.get("page") || "1", 10);
    setFilters(prev => ({
      ...prev,
      page: currentPage
    }));
  }, [searchParams]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showApiError(null, error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Fetch transactions when filters change (but not page changes)
  const fetchTransactions = useCallback(async () => {
    const params = {};
    
    if (filters.status !== "all") {
      params.status = filters.status.toUpperCase();
    }
    
    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    // Always fetch first page with large per_page for client-side pagination
    params.page = 1;
    params.per_page = 100;

    try {
      await dispatch(getTransactionHistory(params));
    } catch (error) {
      console.log('Transaction history fetch error:', error);
    }
  }, [dispatch, filters.status, filters.search]); // Removed filters.page and filters.dateRange from dependencies

  // Initial fetch
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearTransactionHistory());
    };
  }, [dispatch]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset page when changing filters (except pagination)
      page: key === 'page' ? value : 1
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: "all",
      dateRange: "all", 
      search: "",
      page: 1,
      perPage: 100
    });
  };

  const refreshTransactions = () => {
    fetchTransactions();
  };

  // Calculate stats from current transactions
  const getStats = () => {
    const total = transactionHistory.length;
    // Since the new API only returns completed transactions, all are fulfilled
    const fulfilled = total;
    const cancelled = 0;
    const initiated = 0;
    
    const totalRevenue = transactionHistory
      .reduce((sum, t) => sum + parseFloat(t.amounts?.final || 0), 0);
    
    const totalDiscounts = transactionHistory
      .reduce((sum, t) => sum + parseFloat(t.amounts?.discount || 0), 0);

    return { 
      total, 
      fulfilled, 
      cancelled, 
      initiated, 
      totalRevenue, 
      totalDiscounts 
    };
  };

  return {
    // State
    transactions: transactionHistory,
    meta: historyMeta,
    loading: historyLoading,
    filters,
    stats: getStats(),
    
    // Actions
    updateFilter,
    resetFilters,
    refreshTransactions,
    fetchTransactions
  };
};